import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheck, FiChevronDown, FiPhone, FiCalendar, FiPackage, FiStar, FiArrowRight } from 'react-icons/fi';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FormulasPage({ site, primaryColor = '#D4AF37', onBooking }) {
  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('single');
  const [expandedFormula, setExpandedFormula] = useState(null);

  useEffect(() => {
    loadFormulas();
  }, []);

  const loadFormulas = async () => {
    try {
      const response = await axios.get(`${API}/api/global-formulas`);
      setFormulas(response.data || []);
    } catch (error) {
      console.error('Error loading formulas:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'single', label: 'Produits seuls', icon: FiPackage },
    { id: 'combo', label: 'Packs Duo', icon: FiStar },
    { id: 'triple', label: 'Packs Complets', icon: FiStar }
  ];

  const filteredFormulas = formulas.filter(f => f.category === activeCategory);

  const handleSelectOption = (formula, option) => {
    if (onBooking) {
      onBooking({
        formulaId: formula.id,
        formulaName: formula.name,
        optionName: option.name,
        price: option.price,
        duration: option.duration,
        extras: option.extras || [],
        allExtras: formula.all_extras || []
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black py-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Nos Formules
          </h1>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Choisissez la formule qui correspond à votre événement
          </p>
        </motion.div>

        {/* Category Tabs */}
        <div className="flex justify-center gap-2 md:gap-4 mb-10 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-full font-medium transition-all ${
                activeCategory === cat.id
                  ? 'text-black shadow-lg'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              style={activeCategory === cat.id ? { backgroundColor: primaryColor } : {}}
            >
              <cat.icon className="w-4 h-4" />
              <span className="text-sm md:text-base">{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Formulas Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {filteredFormulas.map((formula, idx) => (
              <motion.div
                key={formula.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden"
              >
                {/* Formula Header */}
                <button
                  onClick={() => setExpandedFormula(expandedFormula === formula.id ? null : formula.id)}
                  className="w-full p-6 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                >
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-1">
                      {formula.name}
                    </h3>
                    {formula.subtitle && (
                      <p className="text-sm text-white/50 italic">({formula.subtitle})</p>
                    )}
                    {formula.all_extras && formula.all_extras.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formula.all_extras.map((extra, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                            <FiCheck className="w-3 h-3" /> {extra}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <FiChevronDown 
                    className={`w-6 h-6 text-white/50 transition-transform ${
                      expandedFormula === formula.id ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Options Grid */}
                <AnimatePresence>
                  {expandedFormula === formula.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 md:p-6 pt-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {formula.options?.map((option, optIdx) => (
                          <motion.div
                            key={optIdx}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: optIdx * 0.05 }}
                            className="bg-black/40 rounded-xl p-5 border border-white/10 hover:border-white/30 transition-all group"
                          >
                            <div className="text-center">
                              <h4 className="font-bold text-white text-lg mb-1">
                                "{option.name}"
                              </h4>
                              {option.description && (
                                <p className="text-sm text-white/60 mb-2">{option.description}</p>
                              )}
                              <p className="text-white/70 text-sm mb-3">
                                {option.duration} de location
                              </p>
                              <p className="text-3xl font-black mb-4" style={{ color: primaryColor }}>
                                {option.price}€
                              </p>
                              
                              {/* Option-specific extras */}
                              {option.extras && option.extras.length > 0 && (
                                <div className="space-y-1 mb-4">
                                  {option.extras.map((extra, i) => (
                                    <div key={i} className="flex items-center justify-center gap-1 text-xs text-green-400">
                                      <FiCheck className="w-3 h-3" /> {extra}
                                    </div>
                                  ))}
                                </div>
                              )}

                              <button
                                onClick={() => handleSelectOption(formula, option)}
                                className="w-full py-2.5 rounded-lg font-semibold text-sm text-black group-hover:scale-105 transition-transform flex items-center justify-center gap-2"
                                style={{ backgroundColor: primaryColor }}
                              >
                                Choisir <FiArrowRight className="w-4 h-4" />
                              </button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}

            {filteredFormulas.length === 0 && (
              <div className="text-center py-12 text-white/50">
                Aucune formule dans cette catégorie
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center"
        >
          <p className="text-white/60 mb-4">
            Besoin d'aide pour choisir ? Contactez-nous !
          </p>
          <a
            href={`tel:${site?.contact_info?.phone?.replace(/\s/g, '') || ''}`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-black"
            style={{ backgroundColor: primaryColor }}
          >
            <FiPhone className="w-5 h-5" />
            {site?.contact_info?.phone || 'Appelez-nous'}
          </a>
        </motion.div>
      </div>
    </div>
  );
}
